import React, { useRef } from 'react';
import { clsx } from 'clsx';
import { WizardContent } from '../../wizard';
import type { WizardContentProps } from '../../types';
import wizardStyles from '../../wizard.module.scss';
import styles from './sun-kilometers.module.scss';
import { SunKilometersImage } from './sun-kilometers-image';
import { SunKilometersChart } from './sun-kilometers-chart';
import { SliderVisual } from '@components/slider-visual/slider-visual';
import { useIsAnswered } from '@hooks/use-is-answered';
import { QuestionCard } from '@components/question-card/question-card';

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export const SunKilometersWizardContent: React.FC<
  WizardContentProps<PvWizardResultSectionSunKilometers>
> = ({
  questions,
  onAnswerUpdated,
  nextStep,
  previousStep,
  wizardState,
  result,
  sectionKey,
  sectionInfo,
  showNextButton
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isQuestionAnswered = useIsAnswered<PvWizardQuestion>({
    questions,
    answers: wizardState.answers,
    activeSlide: wizardState.activeSlide
  });

  const showCarImage = wizardState.activeSlide === 0 || wizardState.activeSlide === 1;
  const carType = capitalize(wizardState.answers.vehicleType?.toLowerCase() || 'default');
  const houseType = capitalize(wizardState.answers.houseType?.toLowerCase() || 'einfamilienhaus');

  return (
    <WizardContent
      classNameItemTop={clsx({ [wizardStyles.wizardItemTop]: !showCarImage })}
      itemTop={
        <SliderVisual
          containerAsRow={!showCarImage}
          summaryValue={`Ca. ${result?.summary.value || 0}`}
          summaryLabel={'Sonnenkilometer'}
          subline={`${result?.summary.subline || 0} km Fahrleistung pro Jahr`}
          showResults={wizardState.activeSlide >= 2}
          sectionInfo={sectionInfo}
          modalRef={ref}
        >
          <>
            {showCarImage && (
              <div className={styles.imgContainer}>
                <SunKilometersImage
                  className={clsx({
                    [styles.img]: true,
                    [styles[`houseType${houseType}`]]: true,
                    [styles[`carType${carType}`]]: true,
                    [styles.showWallbox]: wizardState.answers.hasVehicle === 'yes',
                    [styles.hasNoCar]: wizardState.answers.hasVehicle === 'no'
                  })}
                />
              </div>
            )}
            {wizardState.activeSlide >= 2 && <SunKilometersChart result={result} />}
          </>
        </SliderVisual>
      }
      itemBottom={
        <QuestionCard
          sectionKey={sectionKey}
          activeQuestion={questions[wizardState.activeSlide]}
          onAnswerUpdated={onAnswerUpdated}
          previousStep={previousStep}
          nextStep={nextStep}
          showNextButton={isQuestionAnswered && showNextButton}
          wizardState={wizardState}
          showBackButton={sectionKey !== 'startScreen'}
        />
      }
      ref={ref}
    />
  );
};
